import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import {
  fetchPageStructures,
  fetchPageStructureById,
  createPageStructure,
  updatePageStructure,
  updatePageStructureSection,
  updatePageStructureRow,
  getCachedPageStructures,
  getCachedPageStructure,
  clearPageStructureCache,
  pageStructureStore,
  PageStructure
} from "@/lib/stores/pageStructureStore";

interface UsePageStructureDataOptions {
  userId?: string; // Optional: if not provided, uses current user
  structureId?: string; // Optional: if provided, fetches specific structure
  forceReload?: boolean;
  autoLoad?: boolean;
}

interface UsePageStructureDataReturn {
  // Data
  pageStructures: PageStructure[];
  currentPageStructure: PageStructure | null;
  
  // Loading states
  isLoading: boolean;
  /** True once the first loadData() call has completed (success or error) */
  isInitialized: boolean;
  
  // Actions
  loadData: (forceReload?: boolean) => Promise<void>;
  loadStructureById: (id: string) => Promise<PageStructure | null>;
  createStructure: (pageStructure: Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>) => Promise<PageStructure>;
  updateStructure: (id: string, pageStructure: Partial<Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>>) => Promise<PageStructure>;
  updateSection: (id: string, section: 'home' | 'text_management' | 'documents' | 'glossary' | 'blueprints' | 'typesandtags' | 'settings' | 'tms_page' | 'library', sectionData: Record<string, unknown> | null) => Promise<PageStructure>;
  updateRow: (id: string, section: 'home' | 'text_management' | 'glossary' | 'blueprints', rowId: string, rowData: Record<string, unknown>) => Promise<PageStructure>;
  clearCache: () => Promise<void>;
  
  // Cached data access
  getCachedData: () => PageStructure[];
  getCachedStructure: (id: string) => PageStructure | null;
}

export function usePageStructureData({
  userId,
  structureId,
  forceReload = false,
  autoLoad = true,
}: UsePageStructureDataOptions = {}): UsePageStructureDataReturn {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [pageStructures, setPageStructures] = useState<PageStructure[]>([]);
  const [currentPageStructure, setCurrentPageStructure] = useState<PageStructure | null>(null);
  // Initialize isLoading to true when autoLoad is false (manual loading mode)
  // This prevents showing "no data" before loadData() is called
  const [isLoading, setIsLoading] = useState(!autoLoad);
  // Tracks whether the first loadData() call has completed (success or error)
  const [isInitialized, setIsInitialized] = useState(false);

  // Determine which user ID to use
  const targetUserId = userId || user?.id;

  // Load page structures data
  const loadData = useCallback(async (forceReloadData = false) => {
    
    if (!targetUserId) {
      console.warn("[usePageStructureData] No user ID available for loading page structures");
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    setIsLoading(true);
    
    try {
      // Fetch page structures from the API (this also updates the store via syncFromRemote)
      const fetchedStructures = await fetchPageStructures(targetUserId, toast);
      console.log("[usePageStructureData] API returned", fetchedStructures?.length ?? 0, "structures");

      // Read from the store (which was just updated by fetchPageStructures)
      const cachedStructures = getCachedPageStructures(targetUserId);
      console.log("[usePageStructureData] Cached structures", cachedStructures?.length ?? 0, "structures");
      // Update local state from the store (single source of truth)
      setPageStructures(cachedStructures);
      console.log("[usePageStructureData] Local state", pageStructures?.length ?? 0, "structures"); 
      // Set current page structure directly (don't rely solely on store subscription)
      let selectedStructure: PageStructure | null = null;
      if (structureId) {
        // If structureId is provided, find that specific structure
        selectedStructure = cachedStructures.find(s => s.id === structureId) || null;
      } else if (cachedStructures.length > 0) {
        // If no structureId provided, use the first available structure
        selectedStructure = cachedStructures[0];
      } else {
        console.log('[usePageStructureData] No structures found');
      }
      
      // Directly set currentPageStructure to avoid race conditions
      // where the store subscription might not fire (e.g., when cached data matches API data)
      setCurrentPageStructure(selectedStructure);
      
    } catch (error) {
      console.error("[usePageStructureData] Error loading page structures:", error);
      // On error, ensure we still set structures to empty to indicate load completed
      setPageStructures([]);
      setCurrentPageStructure(null);
    } finally {
      // React 18 batches these with the state updates above,
      // ensuring currentPageStructure is set in the same render as isLoading=false
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [targetUserId, structureId, toast]);

  // Load specific page structure by ID
  const loadStructureById = useCallback(async (id: string): Promise<PageStructure | null> => {
    if (!targetUserId) {
      console.warn("No user ID available for loading page structure");
      return null;
    }

    setIsLoading(true);
    try {
      const structure = await fetchPageStructureById(id, toast);
      setCurrentPageStructure(structure);
      return structure;
    } catch (error) {
      console.error("Error loading page structure:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [targetUserId, toast]);

  // Create new page structure
  const createStructure = useCallback(async (pageStructure: Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>): Promise<PageStructure> => {
    if (!targetUserId) {
      throw new Error("No user ID available for creating page structure");
    }

    setIsLoading(true);
    try {
      const newStructure = await createPageStructure(pageStructure, toast);
      
      // Update local state
      setPageStructures(prev => [...prev, newStructure]);
      setCurrentPageStructure(newStructure);
      
      return newStructure;
    } catch (error) {
      console.error("Error creating page structure:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [targetUserId, toast]);

  // Update existing page structure
  const updateStructure = useCallback(async (id: string, pageStructure: Partial<Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>>): Promise<PageStructure> => {
    if (!targetUserId) {
      throw new Error("No user ID available for updating page structure");
    }

    setIsLoading(true);
    try {
      const updatedStructure = await updatePageStructure(id, pageStructure, toast);
      
      // Update local state
      setPageStructures(prev => prev.map(s => s.id === id ? updatedStructure : s));
      if (currentPageStructure?.id === id) {
        setCurrentPageStructure(updatedStructure);
      }
      
      return updatedStructure;
    } catch (error) {
      console.error("Error updating page structure:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, toast]);

  // Update a specific section of a page structure
  const updateSection = useCallback(async (id: string, section: 'home' | 'text_management' | 'documents' | 'glossary' | 'blueprints' | 'typesandtags' | 'settings' | 'tms_page' | 'library', sectionData: Record<string, unknown> | null): Promise<PageStructure> => {
    if (!targetUserId) {
      throw new Error("No user ID available for updating page structure section");
    }

    setIsLoading(true);
    try {
      const updatedStructure = await updatePageStructureSection(id, section, sectionData, toast);
      
      // Update local state
      setPageStructures(prev => prev.map(s => s.id === id ? updatedStructure : s));
      if (currentPageStructure?.id === id) {
        setCurrentPageStructure(updatedStructure);
      }
      
      return updatedStructure;
    } catch (error) {
      console.error(`Error updating ${section} section:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, toast]);

  // Update a specific row within a section
  const updateRow = useCallback(async (id: string, section: 'home' | 'text_management' | 'glossary' | 'blueprints' | 'settings', rowId: string, rowData: Record<string, unknown>): Promise<PageStructure> => {
    if (!targetUserId) {
      throw new Error("No user ID available for updating page structure row");
    }

    setIsLoading(true);
    try {
      const updatedStructure = await updatePageStructureRow(id, section, rowId, rowData, toast);
      
      // Update local state
      setPageStructures(prev => prev.map(s => s.id === id ? updatedStructure : s));
      if (currentPageStructure?.id === id) {
        setCurrentPageStructure(updatedStructure);
      }
      
      return updatedStructure;
    } catch (error) {
      console.error(`Error updating row ${rowId} in ${section} section:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, toast]);

  // Clear cache
  const clearCache = useCallback(async () => {
    try {
      // Clear the store data
      await clearPageStructureCache();
      setPageStructures([]);
      setCurrentPageStructure(null);
      toast({
        title: "Cache cleared",
        description: "Page structure cache cleared",
      });
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }, [toast]);

  // Get cached data without loading
  const getCachedData = (): PageStructure[] => {
    if (!targetUserId) return [];
    return getCachedPageStructures(targetUserId);
  };

  // Get cached structure by ID
  const getCachedStructure = (id: string): PageStructure | null => {
    return getCachedPageStructure(id);
  };

  // Auto-load on mount only if we have a user ID
  useEffect(() => {
    if (autoLoad && targetUserId) {
      loadData(forceReload);
    }
  }, [targetUserId, autoLoad, forceReload, loadData]);

  // Load specific structure if structureId is provided
  useEffect(() => {
    if (structureId && targetUserId) {
      loadStructureById(structureId);
    }
  }, [structureId, targetUserId, loadStructureById]);

  // Subscribe to store changes for real-time updates only if we have a user ID
  useEffect(() => {
    if (!targetUserId) return;

    const unsubscribe = pageStructureStore.subscribe(() => {
      const cachedStructures = getCachedPageStructures(targetUserId);
      setPageStructures(cachedStructures);
      
      // Update current page structure if needed
      let selectedStructure: PageStructure | null = null;
      if (structureId) {
        // If structureId is provided, find that specific structure
        selectedStructure = cachedStructures.find(s => s.id === structureId) || null;
      } else if (cachedStructures.length > 0) {
        // If no structureId provided, use the first available structure
        selectedStructure = cachedStructures[0];
      }
      
      setCurrentPageStructure(selectedStructure);
     
    });

    return () => unsubscribe();
  }, [targetUserId, structureId]);

  return {
    // Data
    pageStructures,
    currentPageStructure,
    
    // Loading states
    isLoading,
    isInitialized,
    
    // Actions
    loadData,
    loadStructureById,
    createStructure,
    updateStructure,
    updateSection,
    updateRow,
    clearCache,
    
    // Cached data access
    getCachedData,
    getCachedStructure,
  };
} 